import { useEffect, useState } from "react";
import { ShoppingCart, Smile } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { startHeartbeat } from "@/lib/telemetry";
import { fmtNum } from "@/lib/format";
import { onDisplay } from "@/lib/tpv";
import { api, API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { FileImage } from "@/components/files/FileFields";
import { useFileUrl } from "@/lib/files";
import { resolveTheme, themeVars } from "@/lib/displayTheme";
import { activeTickerTexts, activeSpotlights } from "@/lib/ticker";
import { QRCodeSVG } from "qrcode.react";
import { RoomStage } from "@/components/furniture/RoomStage";
import { CHARITY_VERSE, CHARITY_REF } from "@/components/pos/CharityDialog";
import { HeartHandshake } from "lucide-react";
import { RateBoardDisplay } from "@/components/exchange/RateBoardDisplay";
import { OrdersBoard, useOrdersBoard } from "@/components/wholesale/OrdersBoard";

const PAIR_KEY = "sp_display_pair";
const pair = () => { try { return JSON.parse(localStorage.getItem(PAIR_KEY) || "null"); } catch { return null; } };
const code = () => new URLSearchParams(window.location.search).get("code") || pair()?.code || null;
const tok = () => { const p = pair(); return p && p.code === code() ? p.token : ""; };
const pubUrl = (id) => `${API}/public/display/${code()}/files/${id}?tok=${tok()}`;

function ShopMark({ shop, big }) {
  const cls = big ? "w-40 h-40" : "w-10 h-10";
  if (!shop?.logo_id) return <Logo className={cls} />;
  const inner = code() ? <img src={pubUrl(shop.logo_id)} alt={shop.name} className="w-full h-full object-contain" /> : <FileImage id={shop.logo_id} alt={shop.name} className="w-full h-full object-contain" />;
  return <span className={`${cls} rounded-2xl bg-white grid place-items-center overflow-hidden shrink-0`}>{inner}</span>;
}

function Ads({ ids }) {
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setI((x) => x + 1), 6000); return () => clearInterval(id); }, []);
  const cur = ids[i % ids.length];
  return (
    <div className="absolute inset-0 grid place-items-center bg-black" data-testid="display-ads">
      {code() ? <img key={cur} src={pubUrl(cur)} alt="" className="max-w-full max-h-full object-contain rise" data-testid="display-ad-image" /> : <FileImage key={cur} id={cur} className="max-w-full max-h-full object-contain rise" />}
      <div className="absolute bottom-3 flex gap-1.5">{ids.map((x, n) => <span key={x} className={`w-2 h-2 rounded-full ${n === i % ids.length ? "bg-white" : "bg-white/30"}`} />)}</div>
    </div>
  );
}

function SceneIdle({ scene, shop, t, remote }) {
  const prods = Object.fromEntries((scene.items || []).map((it) => [it.product_id, it.product || {}]));
  return (
    <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-10 gap-4 rise" data-testid="display-scene">
      <div className="w-full max-w-5xl"><RoomStage room={scene.room} items={scene.items || []} products={prods} src={code() ? pubUrl : null} currency={shop?.currency} /></div>
      <div className="flex items-center gap-6"><span className="text-xl cd-text-70">{t("viz_display_caption")}</span><span className="text-5xl font-black cd-accent cd-glow-green" dir="ltr" data-testid="display-scene-total">{fmtNum((scene.items || []).reduce((s, it) => s + (it.product?.price || 0), 0))} {shop?.currency || ""}</span></div>
    </main>
  );
}

function Spotlight({ items, shop, t }) {
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setI((x) => x + 1), 7000); return () => clearInterval(id); }, []);
  const scene = shop?.display_scene;
  const slots = items.length + (scene?.items?.length ? 1 : 0);
  if (scene?.items?.length && i % slots === items.length) return <SceneIdle scene={scene} shop={shop} t={t} />;
  const s = items[i % items.length];
  const cur = shop?.currency || "";
  return (
    <main className="relative z-10 flex-1 grid lg:grid-cols-2 place-items-center px-10 gap-8" data-testid="display-idle">
      <div key={s.id} className={`w-full max-w-xl aspect-square grid place-items-center rise ${shop?.type === "furniture" ? "cd-3d-stage" : "cd-glass overflow-hidden"}`} data-testid="display-spotlight-photo">
        {shop?.type === "furniture" ? (
          <div className="relative w-[82%] cd-3d" data-testid="display-spotlight-3d">
            <div className="cd-glass overflow-hidden relative">{s.image_id ? (code() ? <img src={pubUrl(s.image_id)} alt={s.name} className="w-full h-auto block" /> : <FileImage id={s.image_id} alt={s.name} className="w-full h-auto block" />) : <ShopMark shop={shop} big />}<div className="cd-3d-shine" /></div>
            <div className="cd-3d-shadow" />
          </div>
        ) : (s.image_id ? (code() ? <img src={pubUrl(s.image_id)} alt={s.name} className="w-full h-full object-cover" /> : <FileImage id={s.image_id} alt={s.name} className="w-full h-full object-cover" />) : <ShopMark shop={shop} big />)}
      </div>
      <div key={`t${s.id}`} className="text-start rtl:text-right rise w-full" data-testid="display-spotlight">
        <div className="text-xs uppercase tracking-[.4em] cd-accent2 font-bold">★ {t("spotlight_badge")}</div>
        <div className="text-6xl font-heading mt-3 leading-tight cd-glow-white" data-testid="display-spotlight-name">{s.name}</div>
        {s.note && <div className="text-2xl cd-text-70 mt-3" dir="auto" data-testid="display-spotlight-note">{s.note}</div>}
        <div className="mt-6 text-7xl font-black cd-accent cd-glow-green tabular-nums" dir="ltr" data-testid="display-spotlight-price">{fmtNum(s.price)} <span className="text-3xl cd-text-70">{cur}</span></div>
        {items.length > 1 && <div className="mt-6 flex gap-1.5">{items.map((x, n) => <span key={x.id} className={`w-2 h-2 rounded-full ${n === i % items.length ? "bg-white" : "bg-white/30"}`} />)}</div>}
      </div>
    </main>
  );
}

function Ticker({ shop }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((x) => x + 1), 30000); return () => clearInterval(id); }, []);
  const texts = activeTickerTexts(shop); // eslint-disable-line no-unused-vars
  if (!texts.length) return null;
  const speed = Math.max(8, Math.min(90, Number(shop?.display_ticker_speed) || 30));
  const items = texts.map((x, i) => <span key={i} className="cd-ticker-item" dir="auto">{x}</span>);
  return (
    <div className="cd-ticker relative z-10" data-testid="display-ticker" data-count={texts.length} data-tick={tick} style={{ "--cd-ticker-speed": `${speed * Math.max(1, texts.length / 2)}s` }}>
      <div className="cd-ticker-track">{items}{items}</div>
    </div>
  );
}

function useRemote(setMsg, gen) {
  const [shop, setShop] = useState(null);
  const [lost, setLost] = useState(false);
  const [needAuth, setNeedAuth] = useState(false);
  useEffect(() => {
    const c = code();
    if (!c) return undefined;
    setNeedAuth(false); setShop(null);
    let alive = true, ws = null, poll = null, last = null;
    const apply = (d) => { if (d.shop) setShop(d.shop); if (d.updated_at !== last) { last = d.updated_at; setMsg(d.state?.type ? d.state : null); } };
    const unauth = () => { if (!alive) return; alive = false; if (poll) clearInterval(poll); ws?.close(); setNeedAuth(true); };
    const startPoll = () => {
      if (poll) return;
      const tick = async () => { try { const { data } = await api.get(`/public/display/${c}`, { params: { tok: tok() } }); if (alive) { setLost(false); apply(data); } } catch (e) { if (e?.response?.status === 401) unauth(); else if (alive) setLost(true); } };
      tick(); poll = setInterval(tick, 500);
    };
    const connect = () => {
      if (!alive) return;
      try { ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/display/${c}?tok=${tok()}`); } catch { startPoll(); return; }
      ws.onopen = () => { setLost(false); if (poll) { clearInterval(poll); poll = null; } };
      ws.onmessage = (e) => { try { apply(JSON.parse(e.data)); } catch {} };
      ws.onerror = () => startPoll();
      ws.onclose = (e) => { if (!alive) return; if (e.code === 4401) { unauth(); return; } startPoll(); setTimeout(connect, 4000); };
    };
    connect();
    const ping = setInterval(() => ws?.readyState === 1 && ws.send("p"), 25000);
    return () => { alive = false; clearInterval(ping); if (poll) clearInterval(poll); ws?.close(); };
  }, [setMsg, gen]);
  return [shop, lost, needAuth];
}

function PairScreen({ onPaired, t, needAuth }) {
  const [c, setC] = useState(code() || "");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/public/display/pair", { code: c.trim(), password: pw }); localStorage.setItem(PAIR_KEY, JSON.stringify({ code: c.trim(), token: data.token })); onPaired(); }
    catch (ex) { setErr(t(ex?.response?.status === 401 ? "display_bad_password" : ex?.response?.status === 404 ? "display_bad_code" : "error")); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen cd-root text-white grid place-items-center p-6 relative overflow-hidden" data-testid="display-pair-screen">
      <div className="cd-nebula" aria-hidden="true" /><div className="cd-stars" aria-hidden="true" /><div className="cd-stars cd-stars-2" aria-hidden="true" />
      <div className="cd-orbit-corner" aria-hidden="true"><Logo className="w-80 h-80" /></div>
      <form onSubmit={go} className="relative z-10 w-full max-w-sm rounded-2xl bg-white text-[#12384A] p-6 space-y-4 shadow-[0_30px_80px_-30px_rgba(0,0,0,.8)]">
        <div className="flex items-center gap-2"><Logo className="w-10 h-10" /><div><div className="font-heading text-xl">{t("display_pair_title")}</div><div className="text-xs text-muted-foreground">{t("display_pair_hint")}</div></div></div>
        {needAuth && <p className="text-xs rounded-lg bg-[#FFF7E6] text-[#9a5b00] p-2" data-testid="display-pair-reauth">{t("display_pair_reauth")}</p>}
        <label className="block text-sm font-bold">{t("pairing_code")}<input value={c} onChange={(e) => setC(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" dir="ltr" className="mt-1 w-full h-12 rounded-xl border border-border px-3 font-mono text-2xl tracking-[.3em] text-center" data-testid="display-pair-code" /></label>
        <label className="block text-sm font-bold">{t("display_password")}<input value={pw} onChange={(e) => setPw(e.target.value)} type="password" dir="ltr" className="mt-1 w-full h-12 rounded-xl border border-border px-3" data-testid="display-pair-password" /></label>
        {err && <p className="text-sm text-[#C7353B] font-bold" data-testid="display-pair-error">{err}</p>}
        <button type="submit" disabled={busy || c.length !== 6 || !pw} className="w-full h-12 rounded-xl bg-[#0B2A5B] text-white font-bold disabled:opacity-50" data-testid="display-pair-submit">{t("display_connect")}</button>
      </form>
    </div>
  );
}

export default function CustomerDisplay() {
  const { t, view, lang, user } = useApp();
  const [gen, setGen] = useState(0);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "F4") { e.preventDefault(); window.close(); setTimeout(() => setClosed(true), 50); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const standalone = !user && !code();
  const remote = !!code();
  const [msg, setMsg] = useState(null);
  const [remoteShop, lost, needAuth] = useRemote(setMsg, gen);
  useEffect(() => (remote ? undefined : onDisplay(setMsg)), [remote]);
  const shop = remote ? remoteShop : view.shop;
  useEffect(() => startHeartbeat("display", () => shop?.id), [shop?.id]);
  useEffect(() => {
    if (msg?.type !== "done" || msg.sale_id) return undefined;
    const id = setTimeout(() => setMsg(null), 8000);
    return () => clearTimeout(id);
  }, [msg]);
  const cur = msg?.currency || shop?.currency || "";
  const lines = msg?.type === "cart" ? msg.lines : [];
  const ads = shop?.display_ads || [];
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link?.getAttribute("href");
    if (link) link.setAttribute("href", "/display-manifest.json");
    return () => { if (link && prev) link.setAttribute("href", prev); };
  }, []);
  const [, setClock] = useState(0);
  useEffect(() => { const id = setInterval(() => setClock((x) => x + 1), 30000); return () => clearInterval(id); }, []);
  const spots = activeSpotlights(shop, remote ? null : view.products);
  const theme = resolveTheme(shop?.display_theme);
  const rootStyle = themeVars(theme);
  const localBg = useFileUrl(remote ? null : theme.bg_image_id);
  const bgUrl = theme.bg_image_id ? (remote ? pubUrl(theme.bg_image_id) : localBg) : null;
  const Backdrop = () => (<>{bgUrl && <div className="cd-bg-image" style={{ backgroundImage: `url(${bgUrl})` }} data-testid="display-bg-image" />}{theme.nebula && <div className="cd-nebula" aria-hidden="true" data-testid="display-nebula" />}{theme.stars && <><div className="cd-stars" aria-hidden="true" /><div className="cd-stars cd-stars-2" aria-hidden="true" /></>}</>);
  const fxShop = shop?.type === "exchange";
  const boardOn = shop?.type === "wholesale" && !!shop?.display_orders && !closed;
  const [board, fresh] = useOrdersBoard(boardOn, remote, code(), tok());
  if (standalone || needAuth) return <PairScreen t={t} needAuth={needAuth} onPaired={() => { setGen((g) => g + 1); window.history.replaceState(null, "", `/display?code=${code()}`); }} />;
  if (closed) return <div className="min-h-screen bg-black text-white/40 grid place-items-center text-sm cursor-pointer" onClick={() => setClosed(false)} data-testid="display-closed">{t("display_closed_hint")}</div>;
  if (boardOn) {
    return (
      <div className="cd-root min-h-screen cd-text flex flex-col relative overflow-hidden" style={rootStyle} data-testid="customer-display" data-remote={remote ? "1" : "0"} data-board="1" data-theme={shop?.display_theme?.preset || "orbit"}>
        <Backdrop />
        <header className="relative z-10 h-16 px-6 flex items-center gap-3 border-b cd-border cd-header-bg backdrop-blur-sm" data-testid="display-header">
          <ShopMark shop={shop} /><span className="font-heading text-xl flex-1 truncate">{shop?.name || t("app_name")}</span>
          {remote && <span className={`text-xs px-2 py-1 rounded-full ${lost ? "bg-[#E74C3C]" : "cd-glass-pill"}`} data-testid="display-remote-badge">{lost ? t("display_lost") : `${t("display_paired")} · ${code()}`}</span>}
        </header>
        <OrdersBoard board={board} fresh={fresh} t={t} currency={cur} />
        <Ticker shop={shop} />
        <footer className="relative z-10 h-14 flex items-center justify-center" data-testid="display-powered-by">
          <span className="cd-operated"><Logo className="w-7 h-7" /><span className="cd-operated-text">{t("operated_by")} <b>Orbit PMS</b></span></span>
        </footer>
      </div>
    );
  }
  if (fxShop) {
    return (
      <div className="cd-root min-h-screen cd-text flex flex-col relative overflow-hidden" style={rootStyle} data-testid="customer-display" data-remote={remote ? "1" : "0"} data-fx="1" data-theme={shop?.display_theme?.preset || "orbit"}>
        <Backdrop />
        <header className="relative z-10 h-16 px-6 flex items-center gap-3 border-b cd-border cd-header-bg backdrop-blur-sm" data-testid="display-header">
          <ShopMark shop={shop} /><span className="font-heading text-xl flex-1 truncate">{shop?.name || t("app_name")}</span>
          {remote && <span className={`text-xs px-2 py-1 rounded-full ${lost ? "bg-[#E74C3C]" : "cd-glass-pill"}`} data-testid="display-remote-badge">{lost ? t("display_lost") : `${t("display_paired")} · ${code()}`}</span>}
        </header>
        <RateBoardDisplay shop={shop} t={t} live={msg?.type === "fx" ? msg : null} />
        <Ticker shop={shop} />
        <footer className="relative z-10 h-14 flex items-center justify-center" data-testid="display-powered-by">
          <span className="cd-operated"><Logo className="w-7 h-7" /><span className="cd-operated-text">{t("operated_by")} <b>Orbit PMS</b></span></span>
        </footer>
      </div>
    );
  }
  return (
    <div className="cd-root min-h-screen cd-text flex flex-col relative overflow-hidden" style={rootStyle} data-testid="customer-display" data-remote={remote ? "1" : "0"} data-theme={shop?.display_theme?.preset || "orbit"}>
      <Backdrop />
      <header className="relative z-10 h-16 px-6 flex items-center gap-3 border-b cd-border cd-header-bg backdrop-blur-sm" data-testid="display-header">
        <ShopMark shop={shop} /><span className="font-heading text-xl flex-1 truncate">{msg?.shop || shop?.name || t("app_name")}</span>
        {remote && <span className={`text-xs px-2 py-1 rounded-full ${lost ? "bg-[#E74C3C]" : "cd-glass-pill"}`} data-testid="display-remote-badge">{lost ? t("display_lost") : `${t("display_paired")} · ${code()}`}</span>}
      </header>
      {msg?.type === "charity" ? (
        <main className="relative z-10 flex-1 grid place-items-center text-center px-10 rise" data-testid="display-charity">
          <div className="cd-glass px-12 py-10 max-w-4xl">
            <div className="flex items-center justify-center gap-3 text-3xl font-heading"><HeartHandshake className="w-10 h-10 cd-accent" />{t("charity")}{msg.name ? ` · ${msg.name}` : ""}</div>
            <p className="font-quran text-4xl leading-[1.9] mt-6 cd-glow-white" dir="rtl" lang="ar" data-testid="display-charity-verse">{CHARITY_VERSE}</p>
            <p className="text-xl mt-3 cd-accent font-bold" dir="rtl">{CHARITY_REF}</p>
            {lang !== "ar" && <p className="text-lg mt-4 cd-text-70">{t("charity_verse_tr")}</p>}
            <div className="mt-8 text-5xl font-black cd-accent cd-glow-green tabular-nums" dir="ltr" data-testid="display-charity-amount">{fmtNum(msg.amount)} {msg.currency || ""}</div>
            <div className="mt-2 text-lg cd-text-70">{t("display_charity_hint")}</div>
          </div>
        </main>
      ) : msg?.type === "done" ? (
        <main className="relative z-10 flex-1 grid place-items-center text-center rise" data-testid="display-done">
          <div className="cd-glass px-12 py-10">
            <div className="relative w-28 h-28 mx-auto cd-thanks" data-testid="display-thanks-globe"><span className="cd-sweep" aria-hidden="true" /><span className="cd-sweep cd-sweep-2" aria-hidden="true" /><Logo className="w-28 h-28" /><Smile className="w-12 h-12 absolute -bottom-2 -end-2 cd-accent bg-[#0B1530] rounded-full p-1.5" /></div>
            <div className="text-5xl font-heading mt-5">{t("display_thanks")}</div>
            <div className="text-3xl mt-5 cd-text-80" dir="ltr">{t("total")}: {fmtNum(msg.total)} {cur}</div>
            {msg.change > 0 && <div className="text-5xl font-black cd-accent2 mt-3 cd-glow-amber" data-testid="display-change">{t("change")}: {fmtNum(msg.change)} {cur}</div>}
            {msg.sale_id && <div className="mt-6 flex items-center justify-center gap-4" data-testid="display-receipt-qr">
              <span className="bg-white p-2 rounded-xl"><QRCodeSVG value={`${window.location.origin}/r?id=${msg.sale_id}&lang=${msg.lang || lang}`} size={120} /></span>
              <span className="text-start text-lg cd-text-70 max-w-[220px]">{t("display_scan_receipt")}</span>
            </div>}
            {msg.sale_id && <div className="mt-3 text-sm cd-text-50" data-testid="display-qr-hint">{t("display_qr_stays")}</div>}
          </div>
        </main>
      ) : lines.length ? (
        <main className="relative z-10 flex-1 grid lg:grid-cols-[1fr_440px] gap-4 p-5">
          <ul className="cd-glass p-5 space-y-1 overflow-y-auto" data-testid="display-lines">
            {lines.slice(-10).map((l, i) => (
              <li key={l.name} className={`flex justify-between items-center gap-4 text-2xl rounded-xl px-4 py-3 rise ${i === lines.slice(-10).length - 1 ? "bg-[rgba(var(--cd-accent-rgb),.18)] ring-1 ring-[rgba(var(--cd-accent-rgb),.5)]" : "odd:bg-white/5"}`}>
                <span className="truncate"><span className="inline-grid place-items-center min-w-10 h-10 px-2 me-3 rounded-lg bg-white/10 text-lg font-bold" dir="ltr">{l.qty}{l.kg ? " kg" : ""}</span>{l.name}</span><span dir="ltr" className="font-bold tabular-nums">{fmtNum(l.amount)}</span>
              </li>
            ))}
          </ul>
          <aside className="cd-glass cd-total-card p-8 flex flex-col justify-center gap-2 relative overflow-hidden">
            <div className="cd-orbit-corner" aria-hidden="true" data-testid="display-earth"><Logo className="w-80 h-80" /></div>
            <div className="relative">
              {msg.discount > 0 && <div className="text-2xl cd-accent">{t("discount")}: −{fmtNum(msg.discount)}</div>}
              <div className="text-xl cd-text-60 uppercase tracking-[.3em]">{t("total")}</div>
              <div className="text-7xl font-black tracking-tight cd-accent cd-glow-green tabular-nums" dir="ltr" data-testid="display-total">{fmtNum(msg.total)}</div>
              <div className="text-2xl cd-text-70">{cur}</div>
            </div>
          </aside>
        </main>
      ) : msg?.type === "preview" ? (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-10 gap-4" data-testid="display-preview">
          <div className="w-full max-w-5xl"><RoomStage room={msg.room} items={msg.items || []} products={remote ? Object.fromEntries((msg.items || []).map((it) => [it.product_id, it.product || {}])) : view.products} src={code() ? pubUrl : null} currency={msg.currency} /></div>
          <div className="flex items-center gap-6"><span className="text-xl cd-text-70">{t("viz_display_caption")}</span><span className="text-5xl font-black cd-accent cd-glow-green" dir="ltr" data-testid="display-preview-total">{fmtNum(msg.total)} {msg.currency || ""}</span></div>
        </main>
      ) : spots.length ? (
        <Spotlight items={spots} shop={shop} t={t} />
      ) : shop?.display_scene?.items?.length ? (
        <SceneIdle scene={shop.display_scene} shop={shop} t={t} />
      ) : ads.length ? (
        <main className="relative z-10 flex-1" data-testid="display-idle"><Ads ids={ads} /></main>
      ) : (
        <main className="relative z-10 flex-1 grid lg:grid-cols-2 place-items-center text-center px-8" data-testid="display-idle">
          <div className="relative cd-orbit-hero" data-testid="display-idle-logo">
            <Logo className="w-full h-full" />
            {shop?.logo_id && <div className="absolute -bottom-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2"><ShopMark shop={shop} /></div>}
          </div>
          <div className="text-start rtl:text-right">
            <div className="text-xs uppercase tracking-[.4em] cd-accent-70">Orbit · مدار</div>
            <div className="text-6xl font-heading mt-3 leading-tight cd-glow-white">{t("display_welcome")}</div>
            <div className="text-2xl cd-text-60 mt-4">{shop?.name}</div>
            <div className="mt-8 inline-flex items-center gap-3 cd-text-50 text-lg"><ShoppingCart className="w-7 h-7" />{t("display_idle_hint")}</div>
          </div>
        </main>
      )}
      <Ticker shop={shop} />
      <footer className="relative z-10 h-14 flex items-center justify-center" data-testid="display-powered-by">
        <span className="cd-operated"><Logo className="w-7 h-7" /><span className="cd-operated-text">{t("operated_by")} <b>Orbit PMS</b></span></span>
      </footer>
    </div>
  );
}
